import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import InfoTooltip from '../common/InfoTooltip';

const TRUSS_MIN = 12;
const TRUSS_MAX = 30;
const qualifiesForTrusses = (width) => {
	const w = parseFloat(width) || 0;
	return w >= TRUSS_MIN && w <= TRUSS_MAX;
};

export default function AdditionalOptions({
	handleFilterCategoryId,
	sideWallSidingPanelsData,
	additionalbracing,
	AnchorsOptions,
	panelUpgradeOptions,
	onDataChangeAdditionalOptions,
	leftLean,
	rightLean,
	frontLean,
	backLean,
	centerStorage,
	cSWidth,
	lLWidth,
	rLWidth,
	fLWidth,
	bLWidth,
	sideEndData,
}) {
	// Initialize the form with default values.
	const { watch, register, setValue } = useForm({
		defaultValues: {
			sidewall: 'horizontal',
			gableEndSidingPanels: 'horizontal',
			frameGauge: '1',
			foamClouser: false,
			aditionalBracing: additionalbracing?.[0]?.value || '',
			anchors: AnchorsOptions?.[0]?.value || '',
			panelUpgrade: panelUpgradeOptions?.[0]?.value || '',
			roofInsulationCS: false,
			backInsulationCS: false,
			frontInsulationCS: false,
			leftInsulationCS: false,
			rightInsulationCS: false,
			roofInsulationFS: false,
			roofInsulationLS: false,
			backInsulationLS: false,
			leftInsulationLS: false,
			frontInsulationLS: false,
			roofInsulationRS: false,
			backInsulationRS: false,
			rightInsulationRS: false,
			frontInsulationRS: false,
			trussesCS: '',
			trussesLS: '',
			trussesRS: '',
			trussesFS: '',
			trussesBS: '',
		},
	});

	// Watch all form values.
	const values = watch();

	// Use a deep comparison (using JSON.stringify) in the dependency array
	// to prevent the effect from running on every render.
	useEffect(() => {
		console.log('AdditionalOptions values changed:', values);
		onDataChangeAdditionalOptions(values);
	}, [JSON.stringify(values), onDataChangeAdditionalOptions]);

	// Derive wall presence from sideEndData
	const cfg = sideEndData?.configuration || {};
	const hasSidewall = (section, field) => {
		return cfg[section]?.[field] === 'fullyEnclosed';
	};
	const hasGableEnd = (section, field) => {
		return cfg[section]?.[field] === 'fullyEnclosed';
	};

	const csHasLeftWall = hasSidewall('centerSectionSide', 'leftSidewall');
	const csHasRightWall = hasSidewall('centerSectionSide', 'rightSidewall');
	const csHasFrontWall = hasGableEnd('centerSectionSide', 'frontGableEnd');
	const csHasBackWall = hasGableEnd('centerSectionSide', 'backGableEnd');

	const lsActive = leftLean !== 'none';
	const lsHasLeftWall =
		lsActive && hasSidewall('leftLeanToSide', 'leftSidewall');
	const lsHasFrontWall =
		lsActive && hasGableEnd('leftLeanToSide', 'frontGableEnd');
	const lsHasBackWall =
		lsActive && hasGableEnd('leftLeanToSide', 'backGableEnd');

	const rsActive = rightLean !== 'none';
	const rsHasRightWall =
		rsActive && hasSidewall('rightLeanToSide', 'rightSidewall');
	const rsHasFrontWall =
		rsActive && hasGableEnd('rightLeanToSide', 'frontGableEnd');
	const rsHasBackWall =
		rsActive && hasGableEnd('rightLeanToSide', 'backGableEnd');

	const fsActive = frontLean !== 'none';

	// Reset insulation when structure or wall is removed
	useEffect(() => {
		if (!lsActive) {
			setValue('roofInsulationLS', false);
			setValue('backInsulationLS', false);
			setValue('leftInsulationLS', false);
			setValue('frontInsulationLS', false);
		} else {
			if (!lsHasBackWall) setValue('backInsulationLS', false);
			if (!lsHasLeftWall) setValue('leftInsulationLS', false);
			if (!lsHasFrontWall) setValue('frontInsulationLS', false);
		}
		if (!rsActive) {
			setValue('roofInsulationRS', false);
			setValue('backInsulationRS', false);
			setValue('rightInsulationRS', false);
			setValue('frontInsulationRS', false);
		} else {
			if (!rsHasBackWall) setValue('backInsulationRS', false);
			if (!rsHasRightWall) setValue('rightInsulationRS', false);
			if (!rsHasFrontWall) setValue('frontInsulationRS', false);
		}
		if (!fsActive) setValue('roofInsulationFS', false);
		if (!csHasBackWall) setValue('backInsulationCS', false);
		if (!csHasFrontWall) setValue('frontInsulationCS', false);
		if (!csHasLeftWall) setValue('leftInsulationCS', false);
		if (!csHasRightWall) setValue('rightInsulationCS', false);
	}, [
		lsActive,
		lsHasBackWall,
		lsHasLeftWall,
		lsHasFrontWall,
		rsActive,
		rsHasBackWall,
		rsHasRightWall,
		rsHasFrontWall,
		fsActive,
		csHasBackWall,
		csHasFrontWall,
		csHasLeftWall,
		csHasRightWall,
		setValue,
	]);

	return (
		<div className="new-order-form">
			<p className="instructions-p">
				Select the materials and options for your frame and siding panels.
			</p>

			<div className="field-section-half">
				{/* Only when is closed at one of the sides */}
				<div className="inp-col">
					<label htmlFor="sidewall">
						<strong>Side Wall Siding Panels</strong>
						<InfoTooltip text="Horizontal run length-wise. Vertical run up-and-down for better runoff/cleaner look." />
					</label>
					<select {...register('sidewall')} className="select-input">
						{sideWallSidingPanelsData.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				</div>
				{/* Only when is closed at one of the ends */}
				<div className="inp-col">
					<label htmlFor="gableEndSidingPanels">
						<strong>Gable End Siding Panels</strong>
						<InfoTooltip text="Horizontal run length-wise. Vertical run up-and-down for better runoff/cleaner look." />
					</label>
					<select
						{...register('gableEndSidingPanels')}
						className="select-input"
					>
						{sideWallSidingPanelsData.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				</div>
			</div>
			<hr className="mb-5" />
			<div className="field-section-half">
				<div className="inp-col">
					<label htmlFor="frameGauge">
						<strong>Frame Gauge</strong>
						<InfoTooltip text="12-Gauge is thicker/stronger than standard 14-Gauge and includes a longer rust-through warranty." />
					</label>
					<select
						{...register('frameGauge', { onChange: handleFilterCategoryId })}
						defaultValue="1" // Set default value to 1
					>
						<option value="1">14-Gauge Framing</option>
						<option value="2">12-Gauge Framing</option>
					</select>
				</div>

				<div className="inp-col checkbox-button-list">
					<div className="checkbox-option check-tcolm-lg">
						<label htmlFor="foamClouser">
							<strong>Foam Closure</strong>
							<InfoTooltip text="Seals gaps between roof/wall panels and the frame to block pests and debris." />
						</label>
						<input type="checkbox" {...register('foamClouser')} />
					</div>
				</div>
			</div>
			<hr className="mb-5" />
			<div className="field-section-half">
				<div className="inp-col">
					<label htmlFor="aditionalBracing">
						<strong>Additional Bracing</strong>
						<InfoTooltip text="Extra support for higher wind/snow load resistance." />
					</label>
					<select {...register('aditionalBracing')} className="select-input">
						{additionalbracing.map((option) => (
							<option
								key={option.value}
								value={option.value}
								defaultValue={'None'}
							>
								{option.label}
							</option>
						))}
					</select>
				</div>
				<div className="inp-col">
					<label htmlFor="anchors">
						<strong>Anchors</strong>
						<InfoTooltip text="Select based on foundation: Concrete for slabs, Rebar/Mobile Home for dirt/gravel." />
					</label>
					<select {...register('anchors')} className="select-input">
						{AnchorsOptions?.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				</div>
			</div>
			<hr className="mb-5" />
			<div className="field-section-half">
				<div className="inp-col">
					<label htmlFor="panelUpgrade">
						<strong>Panel Upgrade</strong>
						<InfoTooltip text="26-Gauge is thicker and more durable than standard 29-Gauge panels." />
					</label>
					<select {...register('panelUpgrade')} className="select-input">
						{panelUpgradeOptions?.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				</div>
			</div>
			<hr className="mb-5" />

			{/* Trusses — visible only when structure width is 12–30 ft */}
			{(qualifiesForTrusses(cSWidth) ||
				(leftLean !== 'none' && qualifiesForTrusses(lLWidth)) ||
				(rightLean !== 'none' && qualifiesForTrusses(rLWidth)) ||
				(frontLean !== 'none' && qualifiesForTrusses(fLWidth)) ||
				(backLean !== 'none' && qualifiesForTrusses(bLWidth))) && (
				<>
					<div className="field-section-half">
						<h3 className="font-bold mb-3 w-full">Trusses</h3>
						<InfoTooltip text="Number of trusses to add. Applies when structure width is 12–30 ft. Price is looked up by width and height." />
					</div>
					<div className="field-section-half">
						{qualifiesForTrusses(cSWidth) && (
							<div className="inp-col">
								<label htmlFor="trussesCS">
									<strong>Center Section</strong>
								</label>
								<input
									type="number"
									min="0"
									max="999"
									placeholder="0"
									className="select-input"
									{...register('trussesCS')}
								/>
							</div>
						)}
						{leftLean !== 'none' && qualifiesForTrusses(lLWidth) && (
							<div className="inp-col">
								<label htmlFor="trussesLS">
									<strong>Left Lean-to</strong>
								</label>
								<input
									type="number"
									min="0"
									max="999"
									placeholder="0"
									className="select-input"
									{...register('trussesLS')}
								/>
							</div>
						)}
						{rightLean !== 'none' && qualifiesForTrusses(rLWidth) && (
							<div className="inp-col">
								<label htmlFor="trussesRS">
									<strong>Right Lean-to</strong>
								</label>
								<input
									type="number"
									min="0"
									max="999"
									placeholder="0"
									className="select-input"
									{...register('trussesRS')}
								/>
							</div>
						)}
						{frontLean !== 'none' && qualifiesForTrusses(fLWidth) && (
							<div className="inp-col">
								<label htmlFor="trussesFS">
									<strong>Front Lean-to</strong>
								</label>
								<input
									type="number"
									min="0"
									max="999"
									placeholder="0"
									className="select-input"
									{...register('trussesFS')}
								/>
							</div>
						)}
						{backLean !== 'none' && qualifiesForTrusses(bLWidth) && (
							<div className="inp-col">
								<label htmlFor="trussesBS">
									<strong>Back Lean-to</strong>
								</label>
								<input
									type="number"
									min="0"
									max="999"
									placeholder="0"
									className="select-input"
									{...register('trussesBS')}
								/>
							</div>
						)}
					</div>
					<hr className="mb-5" />
				</>
			)}

			{/* Center Section Insulation */}
			<div className="field-section-half">
				<div className="inp-col">
					<h3 className="font-bold mb-3">Center Section Insulation</h3>
					<ul className="checkbox-button-list check-tcolm w-full">
						<li className="checkbox-option check-tcolm">
							<label htmlFor="roofInsulationCS">Roof</label>
							<input type="checkbox" {...register('roofInsulationCS')} />
						</li>
						{csHasBackWall && (
							<li className="checkbox-option check-tcolm">
								<label htmlFor="backInsulationCS">Back Wall</label>
								<input type="checkbox" {...register('backInsulationCS')} />
							</li>
						)}
						{csHasFrontWall && (
							<li className="checkbox-option check-tcolm">
								<label htmlFor="frontInsulationCS">Front Wall</label>
								<input type="checkbox" {...register('frontInsulationCS')} />
							</li>
						)}
						{csHasLeftWall && (
							<li className="checkbox-option check-tcolm">
								<label htmlFor="leftInsulationCS">Left Sidewall</label>
								<input type="checkbox" {...register('leftInsulationCS')} />
							</li>
						)}
						{csHasRightWall && (
							<li className="checkbox-option check-tcolm">
								<label htmlFor="rightInsulationCS">Right Sidewall</label>
								<input type="checkbox" {...register('rightInsulationCS')} />
							</li>
						)}
					</ul>
				</div>
			</div>

			{/* Lean-to Insulation */}
			{(lsActive || rsActive || fsActive) && (
				<>
					<hr className="mb-5" />
					<div className="field-section-half">
						{lsActive && (
							<div className="inp-col">
								<h3 className="font-bold mb-3">Left Lean-to Insulation</h3>
								<ul className="checkbox-button-list">
									<li className="checkbox-option check-tcolm">
										<label htmlFor="roofInsulationLS">Roof</label>
										<input type="checkbox" {...register('roofInsulationLS')} />
									</li>
									{lsHasBackWall && (
										<li className="checkbox-option check-tcolm">
											<label htmlFor="backInsulationLS">Back Wall</label>
											<input
												type="checkbox"
												{...register('backInsulationLS')}
											/>
										</li>
									)}
									{lsHasLeftWall && (
										<li className="checkbox-option check-tcolm">
											<label htmlFor="leftInsulationLS">Left Sidewall</label>
											<input
												type="checkbox"
												{...register('leftInsulationLS')}
											/>
										</li>
									)}
									{lsHasFrontWall && (
										<li className="checkbox-option check-tcolm">
											<label htmlFor="frontInsulationLS">Front Wall</label>
											<input
												type="checkbox"
												{...register('frontInsulationLS')}
											/>
										</li>
									)}
								</ul>
							</div>
						)}
						{rsActive && (
							<div className="inp-col">
								<h3 className="font-bold mb-3">Right Lean-to Insulation</h3>
								<ul className="checkbox-button-list check-tcolm">
									<li className="checkbox-option">
										<label htmlFor="roofInsulationRS">Roof</label>
										<input type="checkbox" {...register('roofInsulationRS')} />
									</li>
									{rsHasBackWall && (
										<li className="checkbox-option check-tcolm">
											<label htmlFor="backInsulationRS">Back Wall</label>
											<input
												type="checkbox"
												{...register('backInsulationRS')}
											/>
										</li>
									)}
									{rsHasRightWall && (
										<li className="checkbox-option check-tcolm">
											<label htmlFor="rightInsulationRS">Right Sidewall</label>
											<input
												type="checkbox"
												{...register('rightInsulationRS')}
											/>
										</li>
									)}
									{rsHasFrontWall && (
										<li className="checkbox-option check-tcolm">
											<label htmlFor="frontInsulationRS">Front Wall</label>
											<input
												type="checkbox"
												{...register('frontInsulationRS')}
											/>
										</li>
									)}
								</ul>
							</div>
						)}
						{fsActive && (
							<div className="inp-col">
								<h3 className="font-bold mb-3">Front Lean-to Insulation</h3>
								<ul className="checkbox-button-list check-tcolm w-full">
									<li className="checkbox-option">
										<label htmlFor="roofInsulationFS">Roof</label>
										<input type="checkbox" {...register('roofInsulationFS')} />
									</li>
								</ul>
							</div>
						)}
					</div>
				</>
			)}
		</div>
	);
}
